import * as THREE from 'three'
import { SCENE_CONSTANTS } from './constants.js'

export class KeycapRenderer {
  constructor () {
    this.textureCache = new Map()
  }

  createKeycapMaterial (legend, options = {}) {
    const {
      keycapColor = SCENE_CONSTANTS.COLORS.DEFAULT_KEYCAP,
      legendColor = SCENE_CONSTANTS.COLORS.DEFAULT_LEGEND,
      showLegends = true,
      isRightHalf = false
    } = options

    if (showLegends && legend) {
      const texture = this.createKeycapTexture(legend, {
        keycapColor: this.hexToCSS(keycapColor),
        legendColor: this.hexToCSS(legendColor),
        isRightHalf
      })

      return new THREE.MeshStandardMaterial({
        map: texture,
        roughness: SCENE_CONSTANTS.MATERIALS.KEYCAP_ROUGHNESS,
        metalness: SCENE_CONSTANTS.MATERIALS.KEYCAP_METALNESS,
        bumpScale: SCENE_CONSTANTS.MATERIALS.KEYCAP_BUMP_SCALE
      })
    }

    return new THREE.MeshStandardMaterial({
      color: keycapColor,
      roughness: SCENE_CONSTANTS.MATERIALS.KEYCAP_ROUGHNESS,
      metalness: SCENE_CONSTANTS.MATERIALS.KEYCAP_METALNESS,
      bumpScale: SCENE_CONSTANTS.MATERIALS.KEYCAP_BUMP_SCALE
    })
  }

  createKeycapTexture (legend, options = {}) {
    const {
      keycapColor = '#020202',
      legendColor = '#ffffff',
      isRightHalf = false
    } = options

    const cacheKey = `${legend}-${keycapColor}-${legendColor}-${isRightHalf ? 'right' : 'left'}`

    if (this.textureCache.has(cacheKey)) {
      return this.textureCache.get(cacheKey)
    }

    const canvas = this.createTextCanvas(legend, {
      backgroundColor: keycapColor,
      textColor: legendColor,
      isRightHalf
    })

    const texture = new THREE.CanvasTexture(canvas)
    texture.needsUpdate = true
    texture.flipY = false
    texture.colorSpace = THREE.SRGBColorSpace

    this.textureCache.set(cacheKey, texture)
    return texture
  }

  createTextCanvas (text, options = {}) {
    const {
      backgroundColor = '#020202',
      textColor = '#ffffff',
      canvasSize = SCENE_CONSTANTS.TEXTURE.CANVAS_SIZE,
      fontSize = SCENE_CONSTANTS.TEXTURE.FONT_SIZE,
      fontFamily = SCENE_CONSTANTS.TEXTURE.FONT_FAMILY,
      fontWeight = SCENE_CONSTANTS.TEXTURE.FONT_WEIGHT,
      isRightHalf = false
    } = options

    const canvas = document.createElement('canvas')
    const ctx = canvas.getContext('2d')

    canvas.width = canvasSize
    canvas.height = canvasSize

    ctx.fillStyle = backgroundColor
    ctx.fillRect(0, 0, canvas.width, canvas.height)

    if (text && text.length > 0) {
      ctx.fillStyle = textColor
      ctx.font = `${fontWeight} ${fontSize}px ${fontFamily}`
      ctx.textAlign = 'center'
      ctx.textBaseline = 'middle'
      ctx.imageSmoothingEnabled = true

      if (isRightHalf) {
        ctx.save()
        ctx.scale(-1, 1)
        ctx.fillText(text, -canvas.width / 2, canvas.height / 2)
        ctx.restore()
      } else {
        ctx.fillText(text, canvas.width / 2, canvas.height / 2)
      }
    }

    return canvas
  }

  generatePlanarUVs (geometry) {
    if (!geometry.attributes || !geometry.attributes.position) {
      throw new Error('Invalid geometry: missing position attribute')
    }

    geometry.computeBoundingBox()

    if (!geometry.boundingBox) {
      throw new Error('Failed to compute bounding box for geometry')
    }

    const max = geometry.boundingBox.max
    const min = geometry.boundingBox.min

    const offset = new THREE.Vector2(0 - min.x, 0 - min.y)
    const range = new THREE.Vector2(max.x - min.x, max.y - min.y)

    if (range.x === 0 || range.y === 0) {
      throw new Error('Invalid geometry bounds: zero range detected')
    }

    const positions = geometry.attributes.position
    const uvs = new Float32Array(positions.count * 2)

    for (let i = 0; i < positions.count; i++) {
      const x = positions.getX(i)
      const y = positions.getY(i)

      const u = (x + offset.x) / range.x
      const v = 1.0 - (y + offset.y) / range.y

      uvs[i * 2] = u
      uvs[i * 2 + 1] = v
    }

    geometry.setAttribute('uv', new THREE.BufferAttribute(uvs, 2))
  }

  hexToCSS (hexColor) {
    if (typeof hexColor === 'string' && hexColor.startsWith('#')) {
      return hexColor
    }

    if (typeof hexColor === 'number') {
      return '#' + hexColor.toString(16).padStart(6, '0')
    }

    throw new Error(`Invalid hex color format: ${hexColor}`)
  }

  dispose () {
    for (const texture of this.textureCache.values()) {
      texture.dispose()
    }
    this.textureCache.clear()
  }
}

